import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const emptyAvis = { author: "", rating: 5, text: "", date: "", published: true };

export default function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, rating: parseInt(editing.rating, 10) };
      if (editing.id) {
        await api.put(`/admin/testimonials/${editing.id}`, payload);
      } else {
        await api.post("/admin/testimonials", payload);
      }
      toast.success("Avis enregistré.");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Avis supprimé.");
    load();
  };

  const syncGoogle = async () => {
    try {
      const { data } = await api.post("/admin/testimonials/sync-google");
      toast.success(
        `${data.added} nouvel(aux) avis — note Google ${String(data.rating).replace(".", ",")} (${data.total} avis au total)`
      );
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const set = (key) => (e) => setEditing((s) => ({ ...s, [key]: e.target.value }));

  return (
    <div data-testid="testimonials-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-ivory">Avis Google — {items.length}</h2>
        <div className="flex gap-3">
          <button onClick={syncGoogle} className="btn-outline !h-10 !px-5 !text-[11px]" data-testid="avis-sync-button">
            Synchroniser les avis Google
          </button>
          <button onClick={() => setEditing({ ...emptyAvis })} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="avis-add-button">
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Ajouter un avis
          </button>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="avis-edit-modal">
          <form onSubmit={save} className="w-full max-w-xl space-y-4 rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">
                {editing.id ? "Modifier l'avis" : "Nouvel avis"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input required value={editing.author} onChange={set("author")} placeholder="Nom du client" className="input-dark !h-11" data-testid="avis-author-input" />
              <Select value={String(editing.rating)} onValueChange={(v) => setEditing((s) => ({ ...s, rating: v }))}>
                <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="avis-rating-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-coal text-ivory">
                  {[5, 4, 3, 2, 1].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} étoile{n > 1 ? "s" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <textarea rows={3} value={editing.text} onChange={set("text")} placeholder="Texte de l'avis" className="input-dark !h-auto py-3" data-testid="avis-text-input" />
            <input type="date" value={editing.date} onChange={set("date")} className="input-dark !h-11 [color-scheme:dark]" data-testid="avis-date-input" />
            <label className="flex items-center gap-3 text-sm text-ivory/70">
              Publié
              <Switch checked={editing.published} onCheckedChange={(v) => setEditing((s) => ({ ...s, published: v }))} data-testid="avis-published-switch" />
            </label>
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="avis-save-button">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="avis-empty">
          Aucun avis pour le moment. Ajoutez vos meilleurs avis Google.
        </p>
      ) : (
        <div className="space-y-2" data-testid="avis-list">
          {items.map((avis) => (
            <div key={avis.id} className="flex items-center gap-4 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`avis-row-${avis.id}`}>
              <span className="flex shrink-0 items-center gap-1 text-gold">
                {Array.from({ length: avis.rating }).map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-gold" strokeWidth={0} />
                ))}
              </span>
              <span className={`w-32 shrink-0 truncate text-sm ${avis.published ? "text-ivory" : "text-ivory/35"}`}>
                {avis.author}
              </span>
              <span className="flex-1 truncate text-sm text-ivory/50">{avis.text}</span>
              <button onClick={() => setEditing({ ...avis })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`avis-edit-${avis.id}`}>
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
              <button onClick={() => remove(avis.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`avis-delete-${avis.id}`}>
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
